import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { trackGenerateLead } from '../ga';
import './FavoriteSignupModal.css';

const eurPriceFormatter = new Intl.NumberFormat('mt-MT', {
  style: 'currency',
  currency: 'EUR',
});

function isZeroPrice(amount) {
  return amount != null && Number(amount) === 0;
}

function resolvePreviewPrice(priceNow, priceWas) {
  if (isZeroPrice(priceNow)) {
    if (priceWas != null && !isZeroPrice(priceWas)) {
      return eurPriceFormatter.format(Number(priceWas));
    }
    return '?';
  }

  if (priceNow != null && !Number.isNaN(Number(priceNow))) {
    return eurPriceFormatter.format(Number(priceNow));
  }

  if (priceWas != null && !isZeroPrice(priceWas)) {
    return eurPriceFormatter.format(Number(priceWas));
  }

  return '—';
}

/**
 * @param {{
 *   title: string;
 *   image?: string|null;
 *   priceNow?: number|null;
 *   priceWas?: number|null;
 *   merchant?: string|null;
 *   inStock?: boolean|null;
 * }} props
 */
function AlertProductPreview({
  title,
  image,
  priceNow,
  priceWas,
  merchant,
  inStock,
}) {
  const heading =
    typeof title === 'string' && title.trim() !== ''
      ? title.trim()
      : 'This product';
  const merchantText =
    typeof merchant === 'string' ? merchant.trim() : '';
  const showImage = typeof image === 'string' && image.trim().length > 0;
  const priceLabel = resolvePreviewPrice(priceNow, priceWas);

  return (
    <div className="favorite-modal__product">
      <div className="favorite-modal__product-media">
        {showImage ? (
          <img
            className="favorite-modal__product-img"
            src={image}
            alt=""
            loading="lazy"
          />
        ) : (
          <span className="favorite-modal__product-placeholder" aria-hidden="true" />
        )}
      </div>
      <div className="favorite-modal__product-body">
        <p className="favorite-modal__product-title">{heading}</p>
        <p className="favorite-modal__product-price">{priceLabel}</p>
        {inStock === false && (
          <p className="favorite-modal__product-stock">Out of stock</p>
        )}
        {merchantText !== '' && (
          <p className="favorite-modal__product-merchant">{merchantText}</p>
        )}
      </div>
    </div>
  );
}

/**
 * @param {{
 *   onClose: () => void;
 *   productUrl: string;
 *   productTitle: string;
 *   productImage?: string|null;
 *   priceNow?: number|null;
 *   priceWas?: number|null;
 *   merchant?: string|null;
 *   inStock?: boolean|null;
 * }} props
 */
export function FavoriteSignupModal({
  onClose,
  productUrl,
  productTitle,
  productImage = null,
  priceNow = null,
  priceWas = null,
  merchant = null,
  inStock = null,
}) {
  const titleId = useId();
  const emailId = useId();
  const emailRef = useRef(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setEmail('');
    setError(null);
    setSubmitted(false);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (submitted) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      emailRef.current?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [submitted]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitted(false);
    setSubmitting(true);
    try {
      const res = await fetch('/api/product-favorite-signup.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), productUrl }),
      });
      await jsonOrThrow(res);
      setSubmitted(true);
      trackGenerateLead('product_price_alert');
    } catch (err) {
      setError(describeLoadFailure(err));
    } finally {
      setSubmitting(false);
    }
  }

  const isStockStatusAlert = inStock === false;
  const hookLine = isStockStatusAlert
    ? 'Get notified when back in stock!'
    : 'Get notified when price drops';
  const successLine = isStockStatusAlert
    ? "We've saved this alert for you, but unfortunately this functionality isn't built yet. We'll email you once we've finished building it and this product is back in stock."
    : "We've saved this alert for you, but unfortunately this functionality isn't built yet. We'll email you once we've finished building it and the price drops.";

  return createPortal(
    <div
      className="favorite-modal__backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="favorite-modal favorite-modal--product"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="favorite-modal__body">
          <div className="favorite-modal__intro">
            {!submitted ? (
              <h2 id={titleId} className="favorite-modal__hook">
                {hookLine}
              </h2>
            ) : (
              <p
                id={titleId}
                className="favorite-modal__banner favorite-modal__banner--success"
                role="status"
              >
                {successLine}
              </p>
            )}
            <button
              type="button"
              className="favorite-modal__close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <AlertProductPreview
            title={productTitle}
            image={productImage}
            priceNow={priceNow}
            priceWas={priceWas}
            merchant={merchant}
            inStock={inStock}
          />
          {!submitted ? (
            <>
              {error !== null && (
                <p
                  className="favorite-modal__banner favorite-modal__banner--error"
                  role="alert"
                >
                  {error}
                </p>
              )}
              <form className="favorite-modal__form" onSubmit={handleSubmit} noValidate>
                <div className="favorite-modal__field">
                  <label className="sr-only" htmlFor={emailId}>
                    Email
                  </label>
                  <input
                    ref={emailRef}
                    id={emailId}
                    name="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="Email address"
                    autoFocus
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    required
                    maxLength={255}
                    disabled={submitting}
                  />
                </div>
                <div className="favorite-modal__actions">
                  <button
                    type="submit"
                    className="favorite-modal__submit"
                    disabled={submitting}
                  >
                    {submitting ? 'Adding…' : 'Notify me'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="favorite-modal__actions">
              <button
                type="button"
                className="favorite-modal__submit"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
